// ─────────────────────────────────────────────────────────────────────────────
// Formateadores de la consola. Portados TAL CUAL del prototipo aprobado, con
// una sola diferencia: el prototipo congelaba `HOY` en una fecha fija para que
// los datos de muestra siempre se vieran igual; acá «hoy» es hoy.
//
// Todo en es-AR (la consola es interna y siempre estuvo en es-AR; la
// divergencia en-US/es-AR anotada en la bitácora es de la web y la app, no de
// acá).
// ─────────────────────────────────────────────────────────────────────────────

export const nf = (v: any, d = 2): string =>
  Number(v || 0).toLocaleString('es-AR', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

export const usd = (v: any, d = 2): string => '$' + nf(v, d);
export const usd0 = (v: any): string =>
  '$' + Math.round(Number(v) || 0).toLocaleString('es-AR');
export const pct = (v: any, d = 1): string => nf(v, d) + '%';
export const spct = (v: any, d = 2): string =>
  (Number(v) >= 0 ? '+' : '−') + nf(Math.abs(Number(v) || 0), d) + '%';
export const susd = (v: any): string =>
  (Number(v) >= 0 ? '+' : '−') + usd(Math.abs(Number(v) || 0));
export const btc = (v: any): string =>
  '₿ ' +
  Number(v || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
export const sbtc = (v: any): string =>
  (Number(v) >= 0 ? '+' : '−') + btc(Math.abs(Number(v) || 0));
export const qty = (v: any): string =>
  Number(v || 0).toLocaleString('es-AR', { maximumFractionDigits: 8 });

// Decimales por activo: lo que la casa muestra de cada ticker.
export const DEC: Record<string, number> = {
  USD: 2, EUR: 2, CHF: 2, SPY: 4, QQQ: 4, XAU: 4,
  BTC: 8, ETH: 8, SOL: 6, BNB: 6,
};
export const qtyA = (t: string, v: any): string =>
  Number(v || 0).toLocaleString('es-AR', {
    minimumFractionDigits: Math.min(2, DEC[t] ?? 2),
    maximumFractionDigits: DEC[t] ?? 8,
  });

// ── Fechas ───────────────────────────────────────────────────────────────────
// El dato puede llegar como Timestamp de Firestore, {seconds}, Date, string o
// número: el provider ya traduce a Date, pero las lecturas por SDK directo no.
export function toDate(value: any): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const D = (d: any, o?: Intl.DateTimeFormatOptions): string => {
  const x = toDate(d);
  if (!x) return '—';
  return x.toLocaleDateString(
    'es-AR',
    o || { day: '2-digit', month: '2-digit', year: 'numeric' }
  );
};
export const DT = (d: any): string => {
  const x = toDate(d);
  if (!x) return '—';
  return x.toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
};
export const HM = (d: any): string => {
  const x = toDate(d);
  if (!x) return '—';
  return x.toLocaleTimeString('es-AR', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
};
export const dcorto = (d: any): string => {
  const x = toDate(d);
  if (!x) return '—';
  return x
    .toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
    .replace(/\./g, '');
};

export const dur = (ms: number): string => {
  const s = Math.round(ms / 1000);
  if (s < 60) return s + ' s';
  const m = Math.round(s / 60);
  if (m < 60) return m + ' min';
  const h = Math.floor(m / 60);
  return h + ' h ' + (m % 60 ? (m % 60) + ' min' : '');
};

/** Duración PRECISA: «45 s» · «2 m 24 s» · «1 h 26 m».
 *
 *  `dur` redondea al minuto, que sirve para permanencias largas y miente en las
 *  cortas: una visita de 2 m 24 s se lee «2 min» y se pierde justo lo que
 *  distingue una visita real de un rebote. La telemetría se mide con ésta —es
 *  la misma que usaba la página vieja de Métricas (`fmtDur`)—; el resto de la
 *  consola sigue con `dur`. */
export const durP = (ms: number): string => {
  if (!ms || ms <= 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} m ${String(s % 60).padStart(2, '0')} s`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} m`;
};

const diaCal = (d: any): number => {
  const x = toDate(d) ?? new Date();
  const y = new Date(x.getTime());
  y.setHours(0, 0, 0, 0);
  return y.getTime();
};

/** «hoy» / «ayer» / «hace N días» / «hace N meses» / «hace N años». */
export const desde = (d: any): string => {
  if (!toDate(d)) return '—';
  const dd = Math.round((diaCal(new Date()) - diaCal(d)) / 86400000);
  if (dd <= 0) return 'hoy';
  if (dd === 1) return 'ayer';
  if (dd < 30) return 'hace ' + dd + ' días';
  const m = Math.floor(dd / 30);
  if (m < 12) return 'hace ' + m + (m === 1 ? ' mes' : ' meses');
  const y = Math.floor(m / 12);
  return 'hace ' + y + (y === 1 ? ' año' : ' años');
};

/** «en N minutos / horas» hacia adelante. */
export const enCuanto = (d: Date): string => {
  const m = Math.round((d.getTime() - Date.now()) / 60000);
  if (m < 0) return 'ahora';
  if (m < 1) return 'en menos de un minuto';
  if (m < 60) return `en ${m} ${m === 1 ? 'minuto' : 'minutos'}`;
  const h = Math.round(m / 60);
  if (h < 24) return `en ${h} ${h === 1 ? 'hora' : 'horas'}`;
  const dd = Math.round(h / 24);
  return `en ${dd} ${dd === 1 ? 'día' : 'días'}`;
};

/** Iniciales para el avatar (una o dos letras). */
export const ini = (name?: string): string =>
  String(name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';

/** YYYYMMDD en UTC — la misma clave con la que el robot nombra sus snapshots. */
export const ymdUtc = (d: Date): string =>
  d.toISOString().slice(0, 10).replace(/-/g, '');

/** YYYY-MM-DD en hora argentina (UTC−3 fijo) — igual que ingesta y rollup. */
export const dayAR = (d: Date): string =>
  new Date(d.getTime() - 3 * 3600 * 1000).toISOString().slice(0, 10);
